// src/features/services/agent/api/agentApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Agent SKU Builder — FE API layer.
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';
import { createReconnectingEventSource } from '@/utils/sseReconnect';

const BASE = '/agent/threads';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProposedSku {
  sku: string;
  label: string;
  description: string;
  category: string;
  sku_type: 'component' | 'assembly';
  tier: number;
  parent_sku: string | null;
  unit_cost: number | null;
  currency: string | null;
  tags: string[];
  hardware_spec: Record<string, unknown> | null;
}

export interface ProposedBomComponent {
  sku: string;
  quantity: number;
  unit: string;
  label: string;
}

export interface ProposedBom {
  finished_sku: string;
  label: string;
  version: string;
  components: ProposedBomComponent[];
}

export interface Proposal {
  assembly_name: string;
  summary: string;
  skus: ProposedSku[];
  boms: ProposedBom[];
}

export interface ImageAssignment {
  image_index: number;
  filename: string | null;
  assigned_skus: string[];
  description: string | null;
  extracted_specs: Record<string, unknown> | null;
  quarantine_uuid?: string | null;
  quarantine_path?: string | null;
  sha256_short?: string | null;
  byte_length?: number | null;
  /** P17 — Cloud Vision enrichment */
  vision_labels?: string[] | null;
  vision_objects?: string[] | null;
  vision_text?: string | null;
}

/** Base quarantine result shape shared by all media types (DRY). */
export interface QuarantineResultBase {
  uuid: string | null;
  quarantine_path: string | null;
  quarantine_bucket: string | null;
  sha256: string | null;
  sha256_short: string | null;
  filename: string;
  byte_length: number;
  category: string;
  error?: string;
}

export interface ImageQuarantineResult extends QuarantineResultBase {
  index: number;
}

export interface AudioQuarantineResult extends QuarantineResultBase {}

export interface DocumentQuarantineResult extends QuarantineResultBase {}

// ── P17 Vision annotation types ──────────────────────────────────────────

export interface VisionLabel {
  description: string;
  score: number;
  topicality: number;
}

export interface VisionObject {
  name: string;
  score: number;
  boundingPoly: Array<{ x: number; y: number }>;
}

export interface VisionAnnotation {
  index: number;
  labels: VisionLabel[];
  objects: VisionObject[];
  text: string | null;
  error: { code: number; message: string } | null;
}

export type ThreadStatus = 'processing' | 'pending' | 'confirmed' | 'rejected' | 'failed';

export interface AgentThread {
  thread_key: string;
  id_scope: number | null;
  status: ThreadStatus;
  description: string;
  ai_model: string | null;
  category_hint: string | null;
  proposal: Proposal | null;
  image_assignments: ImageAssignment[] | null;
  created_entities: CreatedEntities | null;
  image_count: number;
  image_quarantine: ImageQuarantineResult[] | null;
  audio_quarantine: AudioQuarantineResult | null;
  /** P17 — per-image Cloud Vision annotations (labels, objects, OCR) */
  vision_annotations: VisionAnnotation[] | null;
  error_detail: string | null;
  /** SKU codes that already exist in the catalog — { [sku]: { label, category, active } } */
  duplicate_matches: Record<string, { scope_key: string; label: string; category: string; active: boolean }> | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Telemetry
  trace_id: string | null;
  ai_duration_ms: number | null;
  total_duration_ms: number | null;
  token_estimate: number | null;
  voice_input: boolean;
  /** Per-pipeline-step durations in ms (e.g. { upload: 340, ai: 1200, parse: 80 }) */
  step_timings: Record<string, number> | null;
  /** DAG workflow key linking this thread to the orchestrator workflow */
  dag_workflow_key: string | null;
  /** Present only on 202 response from createThread */
  _stream_url?: string;
}

export interface CreatedEntityResult {
  sku?: string;
  bom_key?: string;
  status: 'created' | 'failed';
  error?: string;
}

export interface CreatedEntities {
  skus: CreatedEntityResult[];
  boms: CreatedEntityResult[];
}

export interface CreateThreadPayload {
  description: string;
  images?: { data: string; mime_type: string; filename?: string }[];
  context?: { category?: string };
  voice_input?: boolean;
  /** Base64-encoded raw audio blob for quarantine archival */
  audio_data?: { data: string; mime_type: string; filename?: string };
}

export interface ThreadListResponse {
  items: AgentThread[];
  count: number;
  cursor: string | null;
}

export interface ConfirmPayload {
  overrides?: {
    skus?: Partial<ProposedSku>[];
  };
}

export interface ConfirmResponse {
  thread_key: string;
  status: 'confirmed';
  created_skus: CreatedEntityResult[];
  created_boms: CreatedEntityResult[];
}

// ── SSE Streaming Types ──────────────────────────────────────────────────

export type PipelineStage =
  | 'validating'
  | 'uploading_media'
  | 'calling_ai'
  | 'parsing_proposal'
  | 'registering_entity'
  | 'complete'
  | 'failed';

export interface StreamStageEvent {
  stage: PipelineStage;
  ts: number;
  trace_id?: string;
  message?: string;
  error?: string;
  sku_count?: number;
  bom_count?: number;
  duplicate_count?: number;
  ai_duration_ms?: number;
  total_duration_ms?: number;
  image_count?: number;
}

export interface StreamDoneEvent {
  ts: number;
}

// ── Query Keys ───────────────────────────────────────────────────────────

export const QK_AGENT_THREADS = ['agent', 'threads'] as const;

// ── API functions ────────────────────────────────────────────────────────

export async function createThreadApi(
  payload: CreateThreadPayload,
): Promise<AgentThread> {
  return bff.post<AgentThread, CreateThreadPayload>(BASE, payload);
}

export async function fetchThreadsApi(
  params?: { status?: string; limit?: number; cursor?: string },
): Promise<ThreadListResponse> {
  return bff.get<ThreadListResponse>(BASE, params as Record<string, string>);
}

export async function fetchThreadApi(key: string): Promise<AgentThread> {
  return bff.get<AgentThread>(`${BASE}/${encodeURIComponent(key)}`);
}

export async function confirmThreadApi(
  key: string,
  body?: ConfirmPayload,
): Promise<ConfirmResponse> {
  return bff.post<ConfirmResponse, ConfirmPayload | undefined>(
    `${BASE}/${encodeURIComponent(key)}/confirm`,
    body,
  );
}

export async function rejectThreadApi(key: string): Promise<AgentThread> {
  return bff.post<AgentThread, undefined>(
    `${BASE}/${encodeURIComponent(key)}/reject`,
    undefined,
  );
}

// ── SSE Stream ───────────────────────────────────────────────────────────

export interface ThreadStreamCallbacks {
  onStage: (event: StreamStageEvent) => void;
  onDone: () => void;
  onError?: (err: unknown) => void;
}

/**
 * Subscribe to real-time pipeline progress for a thread creation.
 * Returns a `close()` handle — call it on unmount / when done.
 */
export function subscribeThreadStream(
  threadKey: string,
  callbacks: ThreadStreamCallbacks,
): { close: () => void } {
  const url = `/bff/agent/threads/${encodeURIComponent(threadKey)}/stream`;

  return createReconnectingEventSource(url, (es) => {
    es.addEventListener('stage', (ev: MessageEvent) => {
      try {
        callbacks.onStage(JSON.parse(ev.data));
      } catch { /* ignore parse errors */ }
    });

    es.addEventListener('done', () => {
      callbacks.onDone();
      es.close();
    });

    // Do not auto-reconnect for agent streams — they are one-shot
    return false;
  });
}
